import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { AccordionTrigger, AccordionContent } from 'components/common/Accordion'
import * as Accordion from '@radix-ui/react-accordion';
import UserForm from './UserForm';
import API from '../../../constants/api';
import PasswordExpiryForm from './PasswordExpiryForm';
import UserList from './UserList';
import zxcvbn from '../../../../../js/zxcvbn';
import LocaleContext from '../../../constants/localization';
import { useDispatch } from 'react-redux';
import { actionError, actionSuccess } from '../../../actions/viewActions';
import { resToJsonHandleErrors } from '../../../helpers/actionHelpers';

const ListView = ({ setSelectedGroup, setSelectView }) => {
  const LS = useContext(LocaleContext);
  const dispatch = useDispatch();

  const [users, setUsers] = useState({});

  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [filter, setFilter] = useState('');
  const [passwordExpiryInMonths, setPasswordExpiryInMonths] = useState(0);
  const [passwordExpiryRadio, setPasswordExpiryRadio] = useState(0);

  const [openAccordions, setOpenAccordions] = useState([]);

  useEffect(() => {
    refreshUsers();
  }, []);

  const refreshUsers = useCallback(async () => {
    await fetch(`${API.DAM.MANAGE_GROUPS}?action=showUsersReact`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
    })
      .then(resToJsonHandleErrors)
      .then((users) => {
        setUsers(users);
      })
      .catch((e) => {
        dispatch(actionError(e));
      });
  }, []);

  const handleNewUser = (e) => {
    e.preventDefault();
    if (zxcvbn(password).score < 3 || password.length < 8) {
      alert('Password too easy');
      return;
    }
    const formData = new URLSearchParams();
    formData.append('login', username);
    formData.append('name', name);
    formData.append('description', description);
    formData.append('email', email);
    formData.append('password', password);

    fetch('manageUsers.do?action=addUserReact', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: formData,
    })
      .then(resToJsonHandleErrors)
      .then((user) => {
        setOpenAccordions(openAccordions.filter((a) => a != 'newuser'));
        setUsers({ ...users, ...user });
        dispatch(actionSuccess('userAdded'));
        clearFields();
      })
      .catch((e) => dispatch(actionError(e)));
  };

  const handlePasswordExpiryAll = (e) => {
    e.preventDefault();
    const formData = new URLSearchParams();
    formData.append('passwordExpiryMonths', passwordExpiryInMonths);

    fetch('manageUsers.do?action=changePasswordExpiryAllReact', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: formData,
    })
      .then(resToJsonHandleErrors)
      .then((j) => {
        setOpenAccordions(openAccordions.filter((a) => a != 'passwordexpiry'));
        dispatch(actionSuccess('passwordExpiryChanged'));
      })
      .catch((e) => dispatch(actionError(e)));
  };

  const handlePasswordExpiryNew = (e) => {
    e.preventDefault();
    const formData = new URLSearchParams();
    formData.append('passwordExpiryMonths', passwordExpiryInMonths);

    fetch('manageUsers.do?action=changePasswordExpiryNewReact', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: formData,
    })
      .then(resToJsonHandleErrors)
      .then((j) => {
        setOpenAccordions(openAccordions.filter((a) => a != 'passwordexpiry'));
        dispatch(actionSuccess('passwordExpiryChanged'));
      })
      .catch((e) => dispatch(actionError(e)));
  };

  const handleToggle = async (id) => {
    setUsers({
      ...users,
      [id]: {
        ...users[id],
        active: !users[id].active,
      },
    });

    const formData = new URLSearchParams();
    formData.append('id', id);

    await fetch('manageUsers.do?action=toggleUserReact', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: formData,
    })
      .then(resToJsonHandleErrors)
      .catch((e) => dispatch(actionError(e)));
  };

  const handleDelete = async (id) => {
    if (!confirm('Haluatko varmasti poistaa käyttäjän?')) return;
    const formData = new URLSearchParams();
    formData.append('id', id);

    await fetch('manageUsers.do?action=deleteUserReact', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: formData,
    })
      .then(resToJsonHandleErrors)
      .then(() => {
        dispatch(actionSuccess('userDeleted'));
        setUsers(
          Object.fromEntries(
            Object.entries(users).filter(([key]) => key !== id)
          )
        );
      })
      .catch((e) => dispatch(actionError(e)));
  };

  const filteredUsers = useMemo(
    () =>
      []
        .concat(Object.entries(users))
        .sort((a, b) =>
          //Number(b[1]?.active) - Number(a[1]?.active) ||
          a[1].username?.localeCompare(b[1]?.username)
        )
        .filter(
          ([, value]) =>
            value.name.toLowerCase().includes(filter.toLowerCase()) ||
            value.username.toLowerCase().includes(filter.toLowerCase())
        ),
    [users, filter]
  );

  const clearFields = () => {
    setUsername('');
    setName('');
    setDescription('');
    setEmail('');
    setPassword('');
  };

  return (
    <div>
      <Accordion.Root
        type="multiple"
        collapsible="true"
        value={openAccordions}
        onValueChange={setOpenAccordions}
      >
        <Accordion.Item value="newuser">
          <AccordionTrigger>
            <span>
              <i className="fa fa-plus-square" />
              {LS.manageUsers.addUser}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <UserForm
              username={username}
              setUsername={setUsername}
              name={name}
              setName={setName}
              description={description}
              setDescription={setDescription}
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              passwordVisible={passwordVisible}
              setPasswordVisible={setPasswordVisible}
              handleNewUser={(e) => handleNewUser(e)}
            />
          </AccordionContent>
        </Accordion.Item>
        <Accordion.Item value="passwordexpiry">
          <AccordionTrigger>
            <span>
              <i className="fa fa-lock" />
              {LS.manageUsers.changePasswordExpiry}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <PasswordExpiryForm
              passwordExpiryInMonths={passwordExpiryInMonths}
              setPasswordExpiryInMonths={setPasswordExpiryInMonths}
              passwordExpiryRadio={passwordExpiryRadio}
              setPasswordExpiryRadio={setPasswordExpiryRadio}
              handlePasswordExpiryNew={handlePasswordExpiryNew}
              handlePasswordExpiryAll={handlePasswordExpiryAll}
            />
          </AccordionContent>
        </Accordion.Item>
      </Accordion.Root>
      <span className="search-bar">
        <i className="fa fa-search" />
        <input
          placeholder={LS.manageUsers.searchUser}
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </span>
      <UserList
        users={filteredUsers}
        handleToggle={handleToggle}
        setSelectView={setSelectView}
        setSelectedGroup={setSelectedGroup}
        handleDelete={handleDelete}
      />
    </div>
  );
};

export default ListView;
